/**
 * ContactEditPopover — the contact-edit form for one outreach row, matching
 * PATCH .../outreach/contact's independent lanes: the creator's emails (pick
 * the primary, or add one — which becomes the primary), phone and whatsapp
 * write the creator PROFILE (global — visible on every campaign that creator
 * is in); emailOverride is this campaign entry only. The creator's time zone lives on their platform
 * profile and is edited in the drawer's Country and time zone section (CreatorGeoSection). Renders as
 * plain content only (no Popover/Dialog chrome of its own, despite the
 * name — "Popover" names the interaction, not the wrapper), so it can be
 * unit-tested standalone. OutreachTab mounts it inside one shared Dialog,
 * opened from either RowActions' pencil icon (editing an existing contact)
 * or ContactCell's "No email found" / "Add email" (a row with none yet).
 *
 * The patch sent to `onSave` only ever carries fields the admin actually
 * touched (diffed against the row's own current values), matching every
 * other outreach mutation in this codebase (a partial PATCH, not a full
 * resource replace) — untouched fields are left alone.
 */
import { useId, useMemo, useState } from 'react';
import type { OutreachContactPatch, OutreachRow } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { CountryCodePicker } from './CountryCodePicker';
import { DEFAULT_COUNTRY_DIAL_CODE } from './countryDialCodes';
import { sourceLabel } from './emailSources';
import { apiErrorCopy } from './sendLabels';

export interface ContactEditPopoverProps {
  row: OutreachRow;
  onSave: (patch: OutreachContactPatch) => void | Promise<void>;
  onClose: () => void;
}

const EMAIL_ID = 'outreach-contact-add-email';
const OVERRIDE_ID = 'outreach-contact-email-override';
const EMAIL_NOTE_ID = 'outreach-contact-email-note';
const PHONE_NOTE_ID = 'outreach-contact-phone-note';
const PHONE_ID = 'outreach-contact-phone';
const WHATSAPP_ID = 'outreach-contact-whatsapp';

/** Digits only — the country-code prefix supplies the leading "+". Drops a
 *  single trunk-prefix "0" (how a local number is conventionally written in
 *  India/UAE/Singapore/UK, e.g. "07911 123456") when combined with a country
 *  code — kept for NANP (+1), where a legitimate 10-digit number never
 *  starts with 0, so there's nothing to strip. Leaving the trunk zero in
 *  would produce a malformed E.164 number ("+4407911123456" vs "+447911123456"). */
function digitsOnly(s: string, countryCode: string): string {
  const digits = s.replace(/\D/g, '');
  if (countryCode !== '+1' && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const INVALID_EMAIL_COPY = "That isn't a valid email address.";

/** National digits (after the trunk zero is dropped) a number may carry, per dial code in the picker. */
const NATIONAL_DIGITS: Record<string, [min: number, max: number]> = {
  '+91': [10, 10],
  '+971': [9, 9],
  '+65': [8, 8],
  '+44': [9, 10],
  '+1': [10, 10],
};

function phoneLengthError(digits: string, countryCode: string): string | null {
  if (!digits) return null;
  const [min, max] = NATIONAL_DIGITS[countryCode] ?? [6, 14];
  if (digits.length >= min && digits.length <= max) return null;
  return min === max ? `Enter ${min} digits for ${countryCode}.` : `Enter ${min} to ${max} digits for ${countryCode}.`;
}

function errorText(err: unknown): string {
  return apiErrorCopy(err, "Couldn't save the contact details. Try again.", 'save');
}

/** Keyed per creator: the drawer swaps `row` in place (Prev/Next) while mounted, and the form's
 *  state is seeded from the row once, so without this it would carry the previous creator's text. */
export function ContactEditPopover(props: ContactEditPopoverProps) {
  return <ContactEditForm key={props.row.entryId} {...props} />;
}

const sameEmail = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

function ContactEditForm({ row, onSave, onClose }: ContactEditPopoverProps) {
  // Baselines are frozen at mount (and advanced on a successful save), not read live from
  // `row`: rows refresh under an open form (SSE, invalidation), and a moving baseline would
  // make an untouched field look edited. The primary comes from row.emails, never row.email:
  // row.email is where mail goes, which is the override when one is set.
  const emails = row.emails ?? [];
  // A list that arrives after the form opened (a search finishing) has no frozen baseline to
  // compare against yet, so an empty baseline falls back to the live primary.
  const livePrimary = emails.find((e) => e.primary)?.address ?? '';
  const radioId = useId();
  const [baseline, setBaseline] = useState(() => ({
    primary: livePrimary,
    override: row.emailOverride ?? '',
  }));
  const [primary, setPrimary] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [emailOverride, setEmailOverride] = useState(baseline.override);
  // "Untouched" is state, not inferred from emptiness: an empty prefilled field the
  // admin never edited must never read as "clear this".
  const [overrideTouched, setOverrideTouched] = useState(false);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_DIAL_CODE);
  const [phone, setPhone] = useState('');
  const [reachableOnWhatsapp, setReachableOnWhatsapp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);


  // Only a field the admin actually touched AND changed goes into the patch. emailOverride
  // clears with null when emptied; the creator's emails have no clear (F9). An added email
  // becomes the primary, so it replaces a pick rather than travel with one (Go refuses both).
  const basePrimary = baseline.primary || livePrimary;
  const selected = primary || basePrimary;
  const trimmedAdd = addEmail.trim();
  const adding = trimmedAdd !== '' && !sameEmail(trimmedAdd, basePrimary);
  const patch = useMemo<OutreachContactPatch>(() => {
    const next: OutreachContactPatch = {};
    if (adding) next.email = trimmedAdd;
    else if (selected && !sameEmail(selected, basePrimary)) next.primaryEmail = selected;
    const trimmedOverride = emailOverride.trim();
    if (overrideTouched && !sameEmail(trimmedOverride, baseline.override)) next.emailOverride = trimmedOverride === '' ? null : trimmedOverride;
    const trimmedPhone = digitsOnly(phone, countryCode);
    if (trimmedPhone) {
      const fullNumber = `${countryCode}${trimmedPhone}`;
      next.phone = fullNumber;
      if (reachableOnWhatsapp) next.whatsapp = fullNumber;
    }
    return next;
  }, [adding, trimmedAdd, selected, basePrimary, emailOverride, overrideTouched, baseline, countryCode, phone, reachableOnWhatsapp]);

  const addEmailError = adding && !EMAIL_SHAPE.test(trimmedAdd) ? INVALID_EMAIL_COPY : null;
  const overrideError = overrideTouched && emailOverride.trim() !== '' && !EMAIL_SHAPE.test(emailOverride.trim()) ? INVALID_EMAIL_COPY : null;
  const phoneError = phoneLengthError(digitsOnly(phone, countryCode), countryCode);
  const isDirty = Object.keys(patch).length > 0 && !addEmailError && !overrideError && !phoneError;

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await onSave(patch);
      // Still mounted (the drawer stays open): what was saved is now the baseline, not a pending edit.
      const savedPrimary = patch.email ?? patch.primaryEmail;
      setBaseline((previous) => ({
        primary: savedPrimary ?? previous.primary,
        override: patch.emailOverride === undefined ? previous.override : (patch.emailOverride ?? ''),
      }));
      if (savedPrimary) setPrimary(savedPrimary);
      setAddEmail('');
      setOverrideTouched(false);
      setPhone('');
      setReachableOnWhatsapp(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <fieldset className="flex flex-col gap-1.5">
        <legend id={`${radioId}-legend`} className="mb-1.5 text-body-sm-medium text-foreground">Primary email</legend>
        {emails.length === 0 ? (
          <p className="text-caption-regular text-muted-foreground">No email yet. Add one below.</p>
        ) : (
          <RadioGroup
            value={adding ? '' : (emails.find((e) => sameEmail(e.address, selected))?.address ?? '')}
            onValueChange={setPrimary}
            disabled={adding}
            aria-labelledby={`${radioId}-legend`}
            aria-describedby={adding ? `${radioId}-locked` : undefined}
            className="gap-1.5"
          >
            {emails.map((e, i) => (
              <div key={e.address.toLowerCase()} className="flex items-center gap-2 text-body-sm-regular">
                <RadioGroupItem value={e.address} id={`${radioId}-${i}`} />
                <Label htmlFor={`${radioId}-${i}`} className="min-w-0 flex-1 cursor-pointer font-normal">
                  <span className="min-w-0 truncate text-foreground">{e.address}</span>
                  <span className="ml-2 shrink-0 text-caption-regular text-muted-foreground">{sourceLabel(e.source)}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
        {adding && !addEmailError && emails.length > 0 && (
          <p id={`${radioId}-locked`} className="text-caption-regular text-muted-foreground">
            Save or clear the new address first.
          </p>
        )}
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={EMAIL_ID}>Add an email</Label>
        <Input
          id={EMAIL_ID}
          type="email"
          value={addEmail}
          onChange={(e) => setAddEmail(e.target.value)}
          placeholder="creator@example.com"
          aria-describedby={EMAIL_NOTE_ID}
          aria-invalid={addEmailError ? true : undefined}
        />
        {addEmailError && <p role="alert" className="text-caption-regular text-destructive">{addEmailError}</p>}
        <p id={EMAIL_NOTE_ID} className="text-caption-regular text-muted-foreground">
          An added email becomes the primary for every campaign this creator is in.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={OVERRIDE_ID}>Email override (only this campaign)</Label>
        <Input
          id={OVERRIDE_ID}
          type="email"
          value={emailOverride}
          onChange={(e) => {
            setEmailOverride(e.target.value);
            setOverrideTouched(true);
          }}
          placeholder="collabs@agency.com"
          aria-invalid={overrideError ? true : undefined}
        />
        {overrideError && <p role="alert" className="text-caption-regular text-destructive">{overrideError}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={PHONE_ID}>Phone</Label>
        <div className="flex">
          <CountryCodePicker value={countryCode} onChange={setCountryCode} />
          <Input
            id={PHONE_ID}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="98765 43210"
            className="rounded-l-none"
            aria-describedby={PHONE_NOTE_ID}
            aria-invalid={phoneError ? true : undefined}
          />
        </div>
        {phoneError && <p role="alert" className="text-caption-regular text-destructive">{phoneError}</p>}
        <p id={PHONE_NOTE_ID} className="text-caption-regular text-muted-foreground">
          Editing phone applies to every campaign this creator is in. Saved phone and WhatsApp aren&apos;t shown here.
        </p>
        <div className="flex items-center gap-2">
          <Checkbox id={WHATSAPP_ID} checked={reachableOnWhatsapp} onCheckedChange={(c) => setReachableOnWhatsapp(c === true)} />
          <Label htmlFor={WHATSAPP_ID} className="font-normal">
            Reachable on WhatsApp
          </Label>
        </div>
      </div>

      {error && <p className="text-body-sm-regular text-destructive">{error}</p>}

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSave} disabled={!isDirty || saving} loading={saving}>
          Save
        </Button>
      </div>
    </div>
  );
}
